import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ListPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface BrownRecord {
  documentNumber: string;
  filingDate: string;
  grantors: string[];
  grantees: string[];
  legalDescription: string;
  parcelId: string;
  township: string;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'FLAGSTAR', 'FIDELITY', 'ROCKET MORTGAGE',
  'QUICKEN LOANS', 'LOANDEPOT', 'MR COOPER', 'CALIBER HOME',
  'JPMORGAN', 'BANK OF AMERICA', 'US BANK', 'U S BANK',
  'GUARANTEED RATE', 'FREEDOM MORTGAGE', 'AMERISAVE',
  'SECRETARY OF HOUSING', 'HOUSING & URBAN', 'HUD',
];

export function BrownCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseBrownData = (text: string): BrownRecord[] => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const records: BrownRecord[] = [];
    
    // Build record blocks by scanning for or/ee/AC patterns
    let currentRecord: Partial<BrownRecord> & { grantors: string[]; grantees: string[] } | null = null;
    const seenDocNumbers = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip VIEW lines
      if (line === 'VIEW') continue;

      // Check if this is a header line (contains a date + LAND CONTRA)
      // Use the date pattern (MM/DD/YYYY) as anchor since "/" doesn't appear in doc numbers
      if (line.includes('LAND CONTRA')) {
        const dateMatch = line.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*LAND CONTRA/);
        if (dateMatch && dateMatch.index !== undefined) {
          // Save previous record
          if (currentRecord?.documentNumber) {
            if (!seenDocNumbers.has(currentRecord.documentNumber)) {
              seenDocNumbers.add(currentRecord.documentNumber);
              records.push(currentRecord as BrownRecord);
            }
          }

          const rawDate = `${dateMatch[1]}/${dateMatch[2]}/${dateMatch[3]}`;
          const dateParts = rawDate.split('/');
          const filingDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;

          // Everything before the date is row# + doc# + book/page (all digits)
          const digitsBeforeDate = line.substring(0, dateMatch.index);
          
          // Extract 12-digit doc number starting with "2025"
          const docMatch = digitsBeforeDate.match(/(2025\d{8})/);
          const docNumber = docMatch ? docMatch[1] : `UNKNOWN_${i}`;

          currentRecord = {
            documentNumber: docNumber,
            filingDate,
            grantors: [],
            grantees: [],
            legalDescription: '',
            parcelId: '',
            township: '',
          };
          continue;
        }
      }

      if (!currentRecord) continue;

      // Grantor line (starts with "or")
      if (line.startsWith('or') && !line.startsWith('order')) {
        const name = line.substring(2).trim();
        if (name) currentRecord.grantors.push(name);
        continue;
      }

      // Grantee line (starts with "ee")
      if (line.startsWith('ee') && !line.startsWith('eel')) {
        const name = line.substring(2).trim();
        if (name) currentRecord.grantees.push(name);
        continue;
      }

      // Legal description line (starts with "AC:")
      if (line.startsWith('AC:')) {
        const legalPart = line.substring(3).trim();
        currentRecord.legalDescription = legalPart;
        
        // Extract township
        const tpMatch = legalPart.match(/TP:\s*(.+?)(?:\s+\d|$)/);
        if (tpMatch) {
          currentRecord.township = tpMatch[1].trim();
        }
        
        // Extract parcel ID (trailing digits, typically 12 digits)
        const parcelMatch = legalPart.match(/(\d{10,15})\s*$/);
        if (parcelMatch) {
          currentRecord.parcelId = parcelMatch[1];
        }
        continue;
      }
    }

    // Don't forget last record
    if (currentRecord?.documentNumber) {
      if (!seenDocNumbers.has(currentRecord.documentNumber)) {
        seenDocNumbers.add(currentRecord.documentNumber);
        records.push(currentRecord as BrownRecord);
      }
    }

    return records;
  };

  const isCorpLender = (name: string): boolean => {
    const upper = name.toUpperCase();
    return CORPORATE_KEYWORDS.some(kw => upper.includes(kw));
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const records = parseBrownData(textData);
      if (records.length === 0) {
        toast({ title: "No records found", description: "Could not parse any LAND CONTRACT records from the pasted text.", variant: "destructive" });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // For land contracts: grantor = seller (who we want to contact), grantee = buyer
      // Filter out corporate sellers (unlikely for land contracts, but just in case)
      const filtered = records.filter(r => {
        const allGrantors = r.grantors.join(' ');
        return !isCorpLender(allGrantors);
      });

      const corporateCount = records.length - filtered.length;
      console.log(`Brown County: Parsed ${records.length} records, ${corporateCount} corporate, ${filtered.length} potential owner-financed`);

      if (filtered.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${records.length} records — all had corporate sellers`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const docNumbers = filtered.map(r => r.documentNumber);
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'Brown')
        .in('document_number', docNumbers);

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = filtered.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${filtered.length} land contracts, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Insert records
      const recordsToInsert = newRecords.map(record => ({
        county: 'Brown',
        document_number: record.documentNumber,
        filing_date: record.filingDate,
        document_type: 'LAND CONTRACT',
        grantor_name: record.grantors.join(', '),
        grantee_name: record.grantees.join(', '),
        legal_description: record.legalDescription,
        parcel_id: record.parcelId || null,
        is_potential_owner_finance: true,
        confidence_score: 100,
        detection_reasons: ['Land Contract document type'],
        status: 'new',
        raw_data: { 
          township: record.township,
          grantors: record.grantors,
          grantees: record.grantees,
        } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} land contract records!\n` +
          `Total parsed: ${records.length} | Corporate filtered: ${corporateCount} | ` +
          `Duplicates skipped: ${existingDocs.size} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Brown County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseBrownData(textData).length : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Brown County, OH - Land Contract Parser</CardTitle>
        <Button
          size="sm"
          onClick={handleProcess}
          disabled={isProcessing || !textData.trim()}
        >
          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Process{previewCount > 0 ? ` (${previewCount})` : ''}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste land contract records from Brown County, OH. The system parses the multi-line format
            with <Badge variant="outline" className="text-xs">or</Badge> (seller) and <Badge variant="outline" className="text-xs">ee</Badge> (buyer) prefixes.
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Format:</strong> Row# + Doc# + Date + LAND CONTRA, then or/ee/AC: lines
          </p>
          <p className="text-sm text-muted-foreground">
            Corporate sellers are automatically filtered out. Duplicate doc numbers are deduplicated.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={"1202500000208538184901/15/2025LAND CONTRA\norA2 PROPERTY SOLUTIONS LLC\neeGILBERT DARYL R\nAC: LOTS SUB: MELDAHL VALLEY TP: LEWIS TOWNSHIP"}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> unique LAND CONTRACT records to import
          </p>
        )}

        <Button
          onClick={handleProcess}
          disabled={isProcessing || !textData.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Processing...
            </>
          ) : (
            <>
              <ListPlus className="h-4 w-4 mr-2" />
              Process Records
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
